import React, { memo, useCallback } from 'react';
import { IconList, IconListNumber } from '../../../icons';
import Tiptap, { useTiptap } from '../Tiptap';
import { type Editor, useEditorState } from '@tiptap/react';
import TiptapToolbarButton from './tiptap-toolbar-button';

const TiptapListBullet = memo(() => {
  const { editor } = useTiptap();

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isBulletList: editor.isActive('bulletList'),
    }),
  });

  const onClick = useCallback(
    () => editor.chain().focus().toggleBulletList().run(),
    [editor],
  );

  return (
    <TiptapToolbarButton
      aria-label="liste à puces"
      tooltip="Liste à puces"
      icon={IconList}
      isActive={editorState.isBulletList}
      onClick={onClick}
    />
  );
});

TiptapListBullet.displayName = 'Tiptap.Toolbar.List.Bullet';

const TiptapListOrdered = memo(() => {
  const { editor } = useTiptap();

  const editorState = useEditorState({
    editor,
    selector: ({ editor }: { editor: Editor }) => ({
      isOrderedList: editor.isActive('orderedList'),
    }),
  });

  const onClick = useCallback(
    () => editor.chain().focus().toggleOrderedList().run(),
    [editor],
  );

  return (
    <TiptapToolbarButton
      aria-label="liste numérotée"
      tooltip="Liste numérotée"
      icon={IconListNumber}
      isActive={editorState.isOrderedList}
      onClick={onClick}
    />
  );
});

TiptapListOrdered.displayName = 'Tiptap.Toolbar.List.Ordered';

const TiptapListGroup = memo(
  ({ ...props }: Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>) => (
    <Tiptap.Toolbar.Group {...props}>
      <TiptapListBullet />
      <TiptapListOrdered />
    </Tiptap.Toolbar.Group>
  ),
);

TiptapListGroup.displayName = 'Tiptap.Toolbar.List';

const TiptapListGroupCompound = Object.assign(TiptapListGroup, {
  Bullet: TiptapListBullet,
  Ordered: TiptapListOrdered,
});

export default TiptapListGroupCompound;
